import { useEffect, useCallback, useRef } from 'react';

import { supabase } from '../../../lib/supabase';
import type { GameRoom } from '../../../types/multiplayer';

import { useGameRoomData } from './useGameRoomData';
import { useRoomGameStart } from './useRoomGameStart';
import { useRoomPlayers } from './useRoomPlayers';
import { useRoomPolling } from './useRoomPolling';
import { useOnMultiplayerResultsScreen } from './useRoomStateScreenGuard';


export function useRoomState(
  roomId: string, 
  userId: string | null, 
  channel: ReturnType<typeof supabase.channel> | null,
  onGameStart?: (sessionId: string) => void
) {
  // Use the new game room data hook
  const {
    currentRoom,
    setCurrentRoom,
    isHost,
    setIsHost,
    categories,
    error,
    setError,
    isLoading,
    setIsLoading,
    fetchRoomData,
  } = useGameRoomData({ roomId, userId });
  
  // Use the new players hook
  const {
    players,
    isReady,
    syncPlayers,
    toggleReady,
  } = useRoomPlayers({ roomId, userId });

  // Simple ref to track component mount state
  const isMounted = useRef(true);
  const gameStartDetected = useRef(false);

  const onResultsScreen = useOnMultiplayerResultsScreen();

  // --- Room polling logic (must be at top level) ---
  const checkRoomStatus = useCallback(async (isBurstPoll = false) => {
    // ... (rest of the code remains the same)
    // Guard: Pause polling if on results screen
    if (onResultsScreen) {
      console.log('[RoomState] Skipping polling: currently on MultiplayerResults screen');
      return;
    }
    // Skip polling if component is unmounted
    if (!isMounted.current || !roomId) return;
    
    const pollType = isBurstPoll ? 'BURST' : 'REGULAR';
    
    try {
      console.log(`[RoomState] ${pollType} polling for room ${roomId} status...`);
      console.log(`[RoomState] Current user: ${userId}, Is host: ${isHost}`);
      
      const { data, error } = await supabase
        .from('multiplayer_game_rooms')
        .select('*')
        .eq('id', roomId)
        .single();
        
      if (error) {
        console.error(`[RoomState] ${pollType} polling error:`, error);
        
        // Handle authentication errors specifically
        if (error.code === '42501' || error.message?.includes('permission denied') || error.code === 'PGRST301') {
          console.warn('[RoomState] Authentication error detected, attempting session recovery...');
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
              console.error('[RoomState] No valid session found, user may need to re-authenticate');
              // You might want to trigger a re-authentication flow here
            } else {
              console.log('[RoomState] Valid session found, continuing...');
            }
          } catch (sessionError) {
            console.error('[RoomState] Session recovery failed:', sessionError);
          }
        }
        return;
      }
      
      // Skip update if no data or no changes
      if (!data) {
        console.log(`[RoomState] No data returned from ${pollType} polling`);
        return;
      }
      
      console.log(`[RoomState] ${pollType} polling result:`, {
        status: data.status,
        sessionId: data.current_session_id,
        updatedAt: data.updated_at,
      });

      const nextIsHost = Boolean(userId && data.host_id === userId);
      if (nextIsHost !== isHost) {
        console.log(`[RoomState] ${pollType} polling updated host authority:`, {
          userId,
          hostId: data.host_id,
          wasHost: isHost,
          nextIsHost,
        });
        setIsHost(nextIsHost);
      }
      
      // Only update if we have a current room to update
      if (currentRoom) {
        // Check if there's an actual change to avoid unnecessary renders
        const hasStatusChange = data.status !== currentRoom.status;
        const hasSessionChange = data.current_session_id !== currentRoom.current_session_id;
        
        if (hasStatusChange || hasSessionChange) {
          console.log(`[RoomState] Room state changed via ${pollType} polling:`, {
            oldStatus: currentRoom.status,
            newStatus: data.status,
            oldSession: currentRoom.current_session_id,
            newSession: data.current_session_id,
            userId: userId,
            isHost: isHost,
            gameStartDetected: gameStartDetected.current
          });
          
          // Update room state with the changes
          const updatedRoom: GameRoom = {
            ...currentRoom,
            ...data,
            status: data.status,
            current_session_id: data.current_session_id,
            updated_at: data.updated_at,
            room_type: data.room_type ?? currentRoom?.room_type,
            family_id: data.family_id ?? currentRoom?.family_id,
            family_name: data.family_name ?? currentRoom?.family_name,
            friends_group_id: data.friends_group_id ?? currentRoom?.friends_group_id,
            friends_group_name: currentRoom?.friends_group_name,
            // Preserve richer fields when polling payload is sparse.
            id: data.id ?? currentRoom?.id ?? roomId,
            name: data.name ?? currentRoom?.name ?? '',
            code: data.code ?? currentRoom?.code ?? '',
            host_id: data.host_id ?? currentRoom?.host_id ?? '',
            max_players: data.max_players ?? currentRoom?.max_players ?? 2,
            is_private: data.is_private ?? currentRoom?.is_private ?? false,
            created_at: data.created_at ?? currentRoom?.created_at ?? new Date().toISOString(),
            category_id: data.category_id ?? currentRoom?.category_id,
            difficulty_id: data.difficulty_id ?? currentRoom?.difficulty_id,
            difficulty: data.difficulty ?? currentRoom?.difficulty,
            time_per_question: data.time_per_question ?? currentRoom?.time_per_question,
            question_count: data.question_count ?? currentRoom?.question_count,
            current_question_index: data.current_question_index ?? currentRoom?.current_question_index,
            player_count: data.player_count ?? currentRoom?.player_count,
            questions: currentRoom?.questions,
          };
          console.log('[useRoomState] setCurrentRoom called:', updatedRoom);
          setCurrentRoom(updatedRoom);
          
          // CRITICAL: Enhanced game start detection with comprehensive logging
          console.log(`[RoomState] ${pollType} polling - Game start condition check:`, {
            roomStatus: data.status,
            hasSessionId: !!data.current_session_id,
            sessionId: data.current_session_id,
            hasOnGameStartCallback: !!onGameStart,
            gameStartAlreadyDetected: gameStartDetected.current,
            userId: userId,
            isHost: isHost,
            roomId: roomId,
            timestamp: new Date().toISOString()
          });
          
          if (data.status === 'in_progress' && data.current_session_id && onGameStart && !gameStartDetected.current) {
            console.log(`🚀 [RoomState] GAME START DETECTED via ${pollType} polling!`);
            console.log('🚀 [RoomState] Session verification:', {
              sessionId: data.current_session_id,
              sessionIdLength: data.current_session_id.length,
              userId: userId,
              isHost: isHost,
              roomId: roomId,
              timestamp: new Date().toISOString()
            });
            
            // Verify session ID is valid before navigation
            if (data.current_session_id && data.current_session_id.length > 0) {
              gameStartDetected.current = true;
              console.log('✅ [RoomState] Session verification passed, triggering navigation');
              console.log('✅ [RoomState] Calling onGameStart with sessionId:', data.current_session_id);
              
              // Add a small delay to ensure state is properly updated
              setTimeout(() => {
                console.log('🏁 [RoomState] Executing onGameStart callback now...');
                onGameStart(data.current_session_id!);
              }, 100);
            } else {
              console.error('❌ [RoomState] Invalid session ID, skipping navigation');
            }
          } else {
            console.log(`[RoomState] ${pollType} polling - Game start conditions not met:`, {
              statusCheck: data.status === 'in_progress',
              sessionCheck: !!data.current_session_id,
              callbackCheck: !!onGameStart,
              notDetectedCheck: !gameStartDetected.current
            });
          }
        } else {
          console.log(`[RoomState] No changes detected in ${pollType} polling`);
        }
      } else {
        // If we have valid data, initialize the room state for the guest
        const initializedRoom: GameRoom = {
          id: data.id ?? roomId,
          name: data.name ?? '',
          code: data.code ?? '',
          host_id: data.host_id ?? '',
          max_players: data.max_players ?? 2,
          is_private: data.is_private ?? false,
          status: data.status,
          created_at: data.created_at ?? new Date().toISOString(),
          updated_at: data.updated_at,
          room_type: data.room_type,
          family_id: data.family_id,
          family_name: data.family_name,
          friends_group_id: data.friends_group_id,
          friends_group_name: currentRoom?.friends_group_name,
          category_id: data.category_id,
          difficulty_id: data.difficulty_id,
          difficulty: data.difficulty,
          time_per_question: data.time_per_question,
          question_count: data.question_count,
          current_session_id: data.current_session_id,
          current_question_index: data.current_question_index ?? 0,
          player_count: data.player_count,
          questions: undefined,
        };

        console.log(`[RoomState] Initializing currentRoom via ${pollType} polling:`, initializedRoom);
        setCurrentRoom(initializedRoom);

        // Optionally, handle navigation/game start here if needed:
        if (data.status === 'in_progress' && data.current_session_id && onGameStart && !gameStartDetected.current) {
          gameStartDetected.current = true;
          console.log('[RoomState] Session verification passed, triggering navigation');
          onGameStart(data.current_session_id);
        }
      }
    } catch (err) {
      console.error(`[RoomState] Error in ${pollType} polling:`, err);
    }
  }, [roomId, currentRoom, setCurrentRoom, onGameStart, userId, isHost]);

  const roomIdRef = useRef(roomId);

  // Enhanced polling hook with faster intervals for real-time game start detection
  const { manualPoll, startBurstPolling } = useRoomPolling(
    () => checkRoomStatus(false), // Use the correctly defined checkRoomStatus
    { burstCount: 8, burstInterval: 500, regularInterval: 1500 } // Faster polling for better real-time detection
  );

  // Game start hook (must be at top level)
  const { startGame } = useRoomGameStart({
    isHost,
    userId,
    roomId,
    currentRoom,
    players,
    setCurrentRoom,
  });
  
  // Update the ref when roomId changes
  useEffect(() => {
    roomIdRef.current = roomId;
  }, [roomId]);
  
  // Main setup effect - runs once on mount
  useEffect(() => {
    if (onResultsScreen) {
      // Guard: If on results screen, do not set up polling or subscriptions
      console.log('[RoomState] Skipping setup: currently on MultiplayerResults screen');
      return;
    }
    if (!roomId) return;
    
    console.log(`[RoomState] Initial setup for room ${roomId}`);
    
    // Set isMounted to true when the component mounts
    isMounted.current = true;
    
    // Track subscriptions for cleanup
    // Use custom hook for player real-time subscription
    // (playerSubscription logic is now handled by usePlayersRealtimeSubscription)

    const initialize = async () => {
      try {
        // Initial data load
        const roomData = await fetchRoomData();
        if (!isMounted.current) return; // Check mount status first

        // Initial player sync
        await syncPlayers();

        if (!roomData) {
          console.warn('[RoomState] No room data fetched initially, but proceeding with player sync and subscriptions.');
          // Decide if you want to return or let subscriptions try to recover state
        }
        
        // CRITICAL: Manual game start detection for guest players
        // This ensures that if the game has already started, the guest player will be navigated immediately
        if (roomData && roomData.status === 'in_progress' && roomData.current_session_id && onGameStart && !gameStartDetected.current) {
          console.log('🔍 [RoomState] Manual game start detection: Game already in progress during initialization!');
          console.log('🔍 [RoomState] Manual session verification:', {
            sessionId: roomData.current_session_id,
            userId: userId,
            isHost: isHost,
            roomId: roomId,
            timestamp: new Date().toISOString()
          });
          
          if (roomData.current_session_id && roomData.current_session_id.length > 0) {
            gameStartDetected.current = true;
            console.log('✅ [RoomState] Manual session verification passed, triggering immediate navigation');
            console.log('🏁 [RoomState] Executing onGameStart callback via manual detection...');
            onGameStart(roomData.current_session_id);
            return; // Exit early since game has started
          }
        }
        
        if (channel) {
          channel.on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'multiplayer_game_rooms',
              filter: `id=eq.${roomId}`,
            },
            (payload: { new: GameRoom; old: GameRoom }) => {
              console.log('[RoomState] Real-time update received for room:', roomId);
              console.log('[RoomState] Full payload:', JSON.stringify(payload, null, 2));
              console.log('[RoomState] Payload event type:', payload.eventType);
              console.log('[RoomState] Payload schema:', payload.schema);
              console.log('[RoomState] Payload table:', payload.table);
              
              if (!isMounted.current) {
                console.log('[RoomState] Component unmounted, ignoring real-time update');
                return;
              }
              
              const updatedRoom = payload.new as GameRoom;
              const oldRoom = payload.old as GameRoom;
              const mergedRoom: GameRoom = {
                ...(currentRoom || {}),
                ...updatedRoom,
                family_name: updatedRoom.family_name ?? currentRoom?.family_name,
                friends_group_name: updatedRoom.friends_group_name ?? currentRoom?.friends_group_name,
              } as GameRoom;
              
              console.log('[RoomState] Real-time room update details:', {
                roomId: mergedRoom.id,
                oldStatus: oldRoom?.status,
                newStatus: mergedRoom.status,
                oldSessionId: oldRoom?.current_session_id,
                newSessionId: mergedRoom.current_session_id,
                userId: userId,
                isHost: isHost,
                timestamp: new Date().toISOString(),
                gameStartDetected: gameStartDetected.current
              });
              
              console.log('[useRoomState] setCurrentRoom called:', mergedRoom);
              setCurrentRoom(mergedRoom);
              
              // CRITICAL: Enhanced game start detection with session verification
              console.log('[RoomState] Checking game start conditions:', {
                roomStatus: mergedRoom.status,
                hasSessionId: !!mergedRoom.current_session_id,
                sessionId: mergedRoom.current_session_id,
                hasOnGameStartCallback: !!onGameStart,
                gameStartAlreadyDetected: gameStartDetected.current,
                userId: userId,
                isHost: isHost
              });
              
              if (mergedRoom.status === 'in_progress' &&
                  mergedRoom.current_session_id &&
                  onGameStart &&
                  !gameStartDetected.current) {
                console.log('[RoomState] 🚀 GAME START DETECTED via real-time subscription!');
                console.log('[RoomState] Session verification:', {
                  sessionId: mergedRoom.current_session_id,
                  sessionIdLength: mergedRoom.current_session_id.length,
                  userId: userId,
                  isHost: isHost,
                  roomId: roomId,
                  timestamp: new Date().toISOString()
                });
                
                // Verify session ID is valid before navigation
                if (mergedRoom.current_session_id && mergedRoom.current_session_id.length > 0) {
                  gameStartDetected.current = true;
                  console.log('[RoomState] ✅ Real-time session verification passed, triggering navigation');
                  console.log('[RoomState] Calling onGameStart with sessionId:', mergedRoom.current_session_id);
                  
                  // Immediate trigger for real-time responsiveness
                  console.log('🏁 [RoomState] Executing onGameStart callback via real-time subscription...');
                  onGameStart(mergedRoom.current_session_id!);
                  
                  // Also trigger burst polling to ensure all clients are synchronized
                  startBurstPolling();
                } else {
                  console.error('[RoomState] ❌ Invalid session ID from real-time update, skipping navigation');
                }
              } else {
                console.log('[RoomState] Game start conditions not met:', {
                  statusCheck: mergedRoom.status === 'in_progress',
                  sessionCheck: !!mergedRoom.current_session_id,
                  callbackCheck: !!onGameStart,
                  notDetectedCheck: !gameStartDetected.current
                });
              }
            }
          );
        }
      } catch (error) {
        console.error('[RoomState] Error in initialization:', error);
        setError(error instanceof Error ? error.message : 'Failed to initialize room');
      }
    };
    
    // Start initialization
    initialize();
    
    // Cleanup function - runs on unmount
    return () => {
      console.log(`[RoomState] Cleanup for room ${roomId}`);
      isMounted.current = false;
      // Reset state
      gameStartDetected.current = false;
      // No need to unsubscribe here, as useRoomChannel handles cleanup
    };
  }, []);

  useEffect(() => {
    // Set isLoading to false as soon as room state is loaded
    if (currentRoom) {
      if (isLoading) {
        setIsLoading(false);
        console.log('[useRoomState] isLoading set to false. Room loaded:', currentRoom);
      }
    }
  }, [currentRoom, isLoading, setIsLoading]);

  return {
    currentRoom,
    players,
    isHost,
    isReady,
    isLoading,
    categories,
    error,
    toggleReady,
    startGame,
    manualPoll,
    startBurstPolling,
  };
}
